import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "@/App.css";
import SmoothScroll from "@/components/mh/SmoothScroll";
import CustomCursor from "@/components/mh/CustomCursor";
import Loader from "@/components/mh/Loader";
import Navbar from "@/components/mh/Navbar";
import Footer from "@/components/mh/Footer";
import ScreenFX from "@/components/mh/ScreenFX";
import Home from "@/pages/Home";

// Loader budgets:
//  - Desktop loader natural runtime is ~4.5s (rAF + exit fade).
//  - Mobile loader is hard-capped internally to 2s.
// The safety net below is a BACKSTOP that only fires if the loader somehow
// never calls onDone (e.g. paused rAF on iOS, a crash in the loader tree).
// It must sit comfortably AFTER the desktop natural completion so we do not
// truncate a healthy exit animation.
const LOADER_SAFETY_DESKTOP_MS = 6000;
const LOADER_SAFETY_MOBILE_MS = 2400;

function App() {
    const [loaded, setLoaded] = useState(false);
    const doneRef = useRef(false);

    const markLoaded = () => {
        if (doneRef.current) return;
        doneRef.current = true;
        setLoaded(true);
    };

    // Lock body scroll while loader is visible.
    useEffect(() => {
        if (!loaded) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
            if (window.__lenis) window.__lenis.resize();
        }
    }, [loaded]);

    // Hard safety net — backstop only. Runs off a wall-clock setTimeout so
    // it is immune to requestAnimationFrame throttling on iOS Safari.
    useEffect(() => {
        const isMobile =
            typeof window !== "undefined" &&
            typeof window.matchMedia === "function" &&
            window.matchMedia("(max-width: 767px)").matches;
        const budget = isMobile ? LOADER_SAFETY_MOBILE_MS : LOADER_SAFETY_DESKTOP_MS;
        const t = setTimeout(() => {
            if (!doneRef.current) {
                document.body.style.overflow = "";
                markLoaded();
            }
        }, budget);
        return () => clearTimeout(t);
    }, []);

    return (
        <BrowserRouter>
            <div className="App relative bg-ink text-fog-primary">
                {/* Grain overlay on top of everything */}
                <div aria-hidden className="grain-overlay" />

                {/* Global CRT / 8-bit screen overlay (scanlines + vignette + aberration) */}
                <ScreenFX />

                {/* Custom cursor (hidden on touch devices via CSS) */}
                <CustomCursor />

                {/* Loader — runs once on first mount regardless of route */}
                {!loaded ? <Loader onDone={markLoaded} /> : null}

                {/* Smooth scroll wrapper */}
                <SmoothScroll>
                    <Navbar />

                    <Routes>
                        <Route path="/" element={<Home loaded={loaded} />} />
                    </Routes>

                    <Footer />
                </SmoothScroll>
            </div>
        </BrowserRouter>
    );
}

export default App;
